import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Palette,
  ImagePlus,
  Check,
  X,
  Upload,
  Loader2,
  LayoutDashboard,
  FolderOpen,
  Users,
  FileText,
  DollarSign,
} from "lucide-react";
import { firmApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import { GetBaseURL } from "../utils/urlUtils";

const PRIMARY_COLOR_PRESETS = [
  { value: "#0F5132", label: "Deep Emerald" },
  { value: "#1E3A5F", label: "Navy Blue" },
  { value: "#0C4A6E", label: "Ocean Blue" },
  { value: "#4A2545", label: "Deep Plum" },
  { value: "#8B2500", label: "Burgundy" },
  { value: "#7F1D1D", label: "Dark Red" },
  { value: "#2D3436", label: "Charcoal" },
  { value: "#1A1A2E", label: "Midnight" },
  { value: "#713F12", label: "Warm Brown" },
  { value: "#3B0764", label: "Deep Violet" },
  { value: "#134E4A", label: "Dark Teal" },
  { value: "#1E1B4B", label: "Indigo" },
];

const ACCENT_COLOR_PRESETS = [
  { value: "#198754", label: "Emerald" },
  { value: "#2563EB", label: "Blue" },
  { value: "#7C3AED", label: "Purple" },
  { value: "#DC2626", label: "Red" },
  { value: "#D97706", label: "Amber" },
  { value: "#0891B2", label: "Cyan" },
  { value: "#65A30D", label: "Lime" },
  { value: "#EC4899", label: "Pink" },
  { value: "#F59E0B", label: "Gold" },
  { value: "#6366F1", label: "Indigo" },
  { value: "#14B8A6", label: "Teal" },
];

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "image/webp"];

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm";

const labelClasses = "block text-sm font-medium text-slate-700 mb-1.5";

const lightenColor = (hex: string, amount: number): string => {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0x00ff) + amount);
  const b = Math.min(255, (num & 0x0000ff) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const BrandingSettings: React.FC = () => {
  const navigate = useNavigate();
  const { firmData, refreshUserData } = useUserDataContext();

  const [primaryColor, setPrimaryColor] = useState(firmData?.branding?.primaryColor || "#0F5132");
  const [accentColor, setAccentColor] = useState(firmData?.branding?.accentColor || "#198754");
  const [logoPreview, setLogoPreview] = useState<string | null>(firmData?.branding?.logoUrl || null);
  // Display URL: use blob URL directly for new uploads, public endpoint for saved logos
  const logoDisplayUrl = logoPreview
    ? logoPreview.startsWith("blob:")
      ? logoPreview
      : firmData?._id
        ? `${GetBaseURL()}/api/firm/logo/${firmData._id}?t=${Date.now()}`
        : null
    : null;
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (firmData?.branding) {
      setPrimaryColor(firmData.branding.primaryColor || "#0F5132");
      setAccentColor(firmData.branding.accentColor || "#198754");
      if (firmData.branding.logoUrl && !logoPreview?.startsWith("blob:")) {
        setLogoPreview(firmData.branding.logoUrl);
      }
    }
  }, [firmData?.branding]);

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleLogoFile = (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(URL.createObjectURL(file));
    setLogoFile(file);
    setSaved(false);
  };

  const removeLogo = () => {
    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
    setLogoFile(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await firmApi.updateProfile({
        branding: {
          primaryColor,
          accentColor,
          logoUrl: logoPreview && !logoPreview.startsWith("blob:") ? logoPreview : firmData?.branding?.logoUrl || null,
        },
      });

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        await firmApi.uploadLogo(formData);
        setLogoFile(null);
      }

      await refreshUserData();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save branding:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Branding & Appearance</h1>
            <p className="text-sm text-slate-500 mt-1">
              Customize your firm's logo, colors, and visual identity across Kavoa.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-all hover:shadow-md disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-8">
        {/* Left column: Settings */}
        <div className="space-y-8">
          {/* Logo */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
              <h3 className="text-base font-semibold text-slate-800">Firm Logo</h3>
            </div>

            <input
              ref={logoInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.svg,.webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleLogoFile(file);
              }}
            />

            {logoPreview ? (
              <div className="relative rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-center gap-5">
                <div className="h-20 w-20 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                  <img src={logoDisplayUrl || undefined} alt="Firm logo" className="max-h-full max-w-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">Logo uploaded</p>
                  <p className="text-xs text-slate-400 mt-0.5">Click below to change or remove</p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file && ACCEPTED_IMAGE_TYPES.includes(file.type)) handleLogoFile(file);
                }}
                onClick={() => logoInputRef.current?.click()}
                className={`cursor-pointer rounded-2xl border-2 border-dashed transition-all p-8 flex flex-col items-center text-center ${
                  dragOver
                    ? "border-brand-accent bg-brand-light/50"
                    : "border-slate-300 bg-slate-50/50 hover:border-brand-light"
                }`}
              >
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-3 ${
                  dragOver ? "bg-brand-light text-brand-accent" : "bg-slate-100 text-slate-400"
                }`}>
                  <ImagePlus className="h-6 w-6" />
                </div>
                <p className="text-sm font-medium text-slate-700">
                  {dragOver ? "Drop your logo here" : "Upload your firm logo"}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Drag & drop or click to browse. JPG, PNG, SVG, or WebP.
                </p>
              </div>
            )}
          </div>

          {/* Primary Color */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <label className={labelClasses}>Primary Color</label>
            <p className="text-xs text-slate-400 mb-3">
              Used for your sidebar background, headers, and buttons.
            </p>
            <div className="flex flex-wrap gap-2.5 mb-4">
              {PRIMARY_COLOR_PRESETS.map((preset) => {
                const isSelected = primaryColor.toUpperCase() === preset.value.toUpperCase();
                return (
                  <button
                    key={preset.value}
                    type="button"
                    title={preset.label}
                    onClick={() => { setPrimaryColor(preset.value); setSaved(false); }}
                    className={`relative h-10 w-10 rounded-full transition-all duration-200 flex items-center justify-center shrink-0 ${
                      isSelected
                        ? "ring-2 ring-offset-2 ring-brand-accent scale-110 shadow-md"
                        : "hover:scale-110 hover:shadow-md ring-1 ring-slate-200"
                    }`}
                    style={{ backgroundColor: preset.value }}
                  >
                    {isSelected && <Check className="h-4 w-4 text-white drop-shadow-sm" />}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => { setPrimaryColor(e.target.value.toUpperCase()); setSaved(false); }}
                className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer appearance-none bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none"
              />
              <input
                type="text"
                className={inputClasses + " flex-1 font-mono"}
                value={primaryColor}
                onChange={(e) => { setPrimaryColor(e.target.value); setSaved(false); }}
                placeholder="#0F5132"
                maxLength={7}
              />
            </div>
          </div>

          {/* Accent Color */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <label className={labelClasses}>Accent Color</label>
            <p className="text-xs text-slate-400 mb-3">
              Used for highlights, active states, and interactive elements.
            </p>
            <div className="flex flex-wrap gap-2.5 mb-4">
              {ACCENT_COLOR_PRESETS.map((preset) => {
                const isSelected = accentColor.toUpperCase() === preset.value.toUpperCase();
                return (
                  <button
                    key={preset.value}
                    type="button"
                    title={preset.label}
                    onClick={() => { setAccentColor(preset.value); setSaved(false); }}
                    className={`relative h-10 w-10 rounded-full transition-all duration-200 flex items-center justify-center shrink-0 ${
                      isSelected
                        ? "ring-2 ring-offset-2 ring-brand-accent scale-110 shadow-md"
                        : "hover:scale-110 hover:shadow-md ring-1 ring-slate-200"
                    }`}
                    style={{ backgroundColor: preset.value }}
                  >
                    {isSelected && (
                      <Check className="h-4 w-4 text-white drop-shadow-sm" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={accentColor}
                onChange={(e) => { setAccentColor(e.target.value.toUpperCase()); setSaved(false); }}
                className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer appearance-none bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none"
              />
              <input
                type="text"
                className={inputClasses + " flex-1 font-mono"}
                value={accentColor}
                onChange={(e) => { setAccentColor(e.target.value); setSaved(false); }}
                placeholder="#198754"
                maxLength={7}
              />
            </div>
          </div>
        </div>

        {/* Right column: Live Preview */}
        <div className="hidden lg:block">
          <label className={labelClasses}>Live Preview</label>
          <div className="sticky top-4">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200" style={{ width: 260 }}>
              <div className="px-4 py-4 flex items-center gap-2.5" style={{ backgroundColor: primaryColor }}>
                {logoPreview ? (
                  <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center overflow-hidden shrink-0">
                    <img src={logoDisplayUrl || undefined} alt="Logo" className="max-h-full max-w-full object-contain" />
                  </div>
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-white/20 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">
                    {firmData?.name || "Your Firm"}
                  </div>
                  <div className="text-[10px] text-white/60 truncate">Legal Practice</div>
                </div>
              </div>
              <div className="h-px" style={{ backgroundColor: lightenColor(primaryColor, 20) }} />
              <div className="py-2 space-y-0.5 px-2" style={{ backgroundColor: primaryColor }}>
                {[
                  { icon: LayoutDashboard, label: "Dashboard", active: true },
                  { icon: FolderOpen, label: "Matters", active: false },
                  { icon: Users, label: "Clients", active: false },
                  { icon: FileText, label: "Documents", active: false },
                  { icon: DollarSign, label: "Billing", active: false },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: item.active ? "rgba(255,255,255,0.15)" : "transparent" }}
                  >
                    <item.icon
                      className="h-3.5 w-3.5 shrink-0"
                      style={{ color: item.active ? accentColor : "rgba(255,255,255,0.5)" }}
                    />
                    <span
                      className="text-[11px] font-medium"
                      style={{ color: item.active ? "#ffffff" : "rgba(255,255,255,0.6)" }}
                    >
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-8" style={{ backgroundColor: primaryColor }} />
              <div
                className="px-4 py-3 flex items-center gap-2.5 border-t"
                style={{ backgroundColor: primaryColor, borderColor: lightenColor(primaryColor, 20) }}
              >
                <div
                  className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                  style={{ backgroundColor: accentColor }}
                >
                  JD
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-medium text-white truncate">Jane Doe</div>
                  <div className="text-[9px] text-white/50 truncate">Administrator</div>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl overflow-hidden shadow-sm border border-slate-200">
              <div className="h-2" style={{ backgroundColor: accentColor }} />
              <div className="bg-white px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-slate-500">Accent preview</span>
                  <div
                    className="px-2 py-0.5 rounded-full text-[9px] font-semibold text-white"
                    style={{ backgroundColor: accentColor }}
                  >
                    Active
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;
